import { Fragment, createElement, type ReactNode } from "react";
import { defaultUrlTransform } from "react-markdown";
import { MarkdownContent } from "@/features/forum/components/MarkdownContent";
import { cn } from "@/utils";

interface JobDescriptionContentProps {
  content: string;
  className?: string;
}

/**
 * A job posting's description, qualifications or perks on the job page.
 *
 * The public jobs API keeps an allow-list of HTML tags in `qualifications` and
 * `perks`, and strips markup from `description`, which leaves text that may be
 * Markdown. HTML is rebuilt as React elements from the allow-list below: only
 * the elements and attributes named here reach the page, and no HTML string is
 * ever injected into it. Everything else renders as Markdown, which shows any
 * HTML as text.
 *
 * Entity-encoded markup is not decoded back into HTML. The API returned it as
 * text, so it renders as text.
 */
export function JobDescriptionContent({ content, className }: JobDescriptionContentProps) {
  const source = content ?? "";
  // `job-rich-text` (global.css) styles headings, lists and links: the site does not load the
  // Tailwind typography plugin, so `prose` alone leaves them unstyled (T100826031).
  if (!looksLikeHtml(source) || typeof DOMParser === "undefined") {
    return <MarkdownContent content={source} className={cn("job-rich-text", className)} />;
  }

  // DOMParser builds an inert document: nothing in it runs or loads.
  const body = new DOMParser().parseFromString(source, "text/html").body;
  return (
    <div className={cn("job-rich-text prose prose-sm dark:prose-invert max-w-none", className)}>
      {renderChildren(body)}
    </div>
  );
}

/** Whether a field is HTML rather than Markdown. Unchanged from the job page's earlier check. */
function looksLikeHtml(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (/^<[a-z][\s\S]*>/i.test(trimmed)) return true;
  return /<\/?(p|br|h[1-6]|ul|ol|li|strong|em|b|i|u|a|span|div|table|tr|td|th|thead|tbody|img|hr|blockquote)(\s[^>]*)?\/?>/i.test(trimmed);
}

const HTML_NAMESPACE = "http://www.w3.org/1999/xhtml";

/** Rendered as themselves. The same tags the backend allows in job-posting HTML. */
const ALLOWED_ELEMENTS = new Set([
  "p", "br", "hr", "span", "div",
  "h1", "h2", "h3", "h4", "h5", "h6",
  "ul", "ol", "li",
  "strong", "b", "em", "i", "u", "s", "del",
  "a", "blockquote", "pre", "code",
  "table", "thead", "tbody", "tr", "th", "td",
]);

const VOID_ELEMENTS = new Set(["br", "hr"]);

/** Removed together with their content, which is code or form state rather than prose. */
const DROPPED_ELEMENTS = new Set(["script", "style", "template", "noscript", "iframe", "textarea", "title"]);

/** Table parts whose whitespace-only text is layout, and is not valid as a DOM child. */
const TABLE_PARTS = new Set(["table", "thead", "tbody", "tr"]);

function renderChildren(parent: Element): ReactNode[] {
  const skipBlankText = TABLE_PARTS.has(parent.localName);
  const rendered: ReactNode[] = [];
  parent.childNodes.forEach((child, index) => {
    if (skipBlankText && child.nodeType === Node.TEXT_NODE && !child.textContent?.trim()) return;
    rendered.push(renderNode(child, index));
  });
  return rendered;
}

/**
 * One parsed node as React. Text stays text, which React escapes. An element
 * outside the allow-list is replaced by its children, and those go through this
 * same function, so every node at every depth is checked. Comments and
 * non-HTML (SVG, MathML) content are dropped.
 */
function renderNode(node: Node, key: number): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) return node.textContent;
  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const element = node as Element;
  const tag = element.localName;
  if (element.namespaceURI !== HTML_NAMESPACE || DROPPED_ELEMENTS.has(tag)) return null;

  const children = renderChildren(element);
  if (!ALLOWED_ELEMENTS.has(tag)) return <Fragment key={key}>{children}</Fragment>;

  const props = { key, ...allowedAttributes(element, tag) };
  return VOID_ELEMENTS.has(tag) ? createElement(tag, props) : createElement(tag, props, ...children);
}

/** The only attributes kept: a link's URL and title, and a table cell's span. */
function allowedAttributes(element: Element, tag: string): Record<string, string | number> {
  const attributes: Record<string, string | number> = {};
  if (tag === "a") {
    // Relative URLs and http(s), mailto, irc(s) and xmpp only: the rule
    // react-markdown applies to every Markdown link. Anything else drops the href.
    const href = defaultUrlTransform((element.getAttribute("href") ?? "").trim());
    if (href) {
      attributes.href = href;
      attributes.target = "_blank";
      attributes.rel = "noopener noreferrer";
    }
    const title = element.getAttribute("title");
    if (title) attributes.title = title;
  } else if (tag === "th" || tag === "td") {
    const colSpan = cellSpan(element.getAttribute("colspan"));
    if (colSpan) attributes.colSpan = colSpan;
    const rowSpan = cellSpan(element.getAttribute("rowspan"));
    if (rowSpan) attributes.rowSpan = rowSpan;
  }
  return attributes;
}

/** A merged-cell span worth keeping: a whole number from 2 to 1000. */
function cellSpan(value: string | null): number | undefined {
  const span = Number(value);
  return Number.isInteger(span) && span > 1 && span <= 1000 ? span : undefined;
}
